import { usePrivy } from '@privy-io/react-auth';
import { useCreateWallet } from '@privy-io/react-auth/solana';
import { useEffect } from 'react';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { usePrivyEmbeddedWallet } from 'src/hooks/wallets/privy/usePrivyEmbeddedWallet';
import useWalletContext from 'src/hooks/useWalletContext';
import { isWalletBlacklistedForEmbeddedWallets } from 'src/constants/wallets';

/**
 * Automatically creates a Privy embedded wallet if one doesn't exist after EOA authentication into Privy.
 * A 2000ms buffer is allowed to ensure the embedded wallet is fetched on the UI if it already exists.
 * Disabled when a blacklisted wallet is connected.
 */
export const useAutoCreatePrivyEmbeddedWallet = () => {
	const { embeddedWallet } = usePrivyEmbeddedWallet();
	const { ready, authenticated, user } = usePrivy();
	const walletContext = useWalletContext();

	const { createWallet } = useCreateWallet();

	const embeddedWalletRef = useSyncedRef(embeddedWallet);
	const createWalletRef = useSyncedRef(createWallet); // there seems to a bug with createWallet constantly being re-created, causing the useEffect to run multiple times unnecessarily

	useEffect(() => {
		const walletAdapterName = walletContext.wallet?.adapter?.name;
		const isBlacklistedWallet =
			isWalletBlacklistedForEmbeddedWallets(walletAdapterName);
		if (isBlacklistedWallet) return;
		if (user && authenticated && ready && !embeddedWallet) {
			setTimeout(() => {
				if (embeddedWalletRef.current) return;
				createWalletRef.current();
			}, 3000);
		}
	}, [
		user,
		authenticated,
		ready,
		embeddedWallet,
		walletContext.wallet?.adapter?.name,
	]);
};
